import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import { useRef, useEffect } from 'react';
import type { ReactElement, RefObject } from 'react';
import styles from './NewsOverview.module.scss';
import { setupTransitionInTimeline } from './NewsOverview.transitions';
import type { NewsOverviewProps } from './NewsOverview.data';
import { useScrollTransition } from 'util/react-transition-component';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import { useChangeTransitionState } from '../../../util/react-transition-component/hooks/useChangeTransitionState';
import NewsOverviewItem from './news-overview-item/NewsOverviewItem';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import PrimaryDropdown from '../../atoms/primary-dropdown/PrimaryDropdown';
import PrimaryPagination from '../../atoms/primary-pagination/PrimaryPagination';
import { useNewsFiltering } from './useNewsFiltering';
import NoFilterResultMessage from '../../atoms/no-filter-result-message/NoFilterResultMessage';
import { useNewsCategoryLabels } from '@/context/newsCategoryLabelsContext';

export default function NewsOverview({
  ref,
  isVisible,
  className,
  blok,
}: NewsOverviewProps): ReactElement {
  const categoryLabels = useNewsCategoryLabels();
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const gridRef = useRef<HTMLDivElement>(null);
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, false);
  const lineRef = useRef<HTMLSpanElement>(null);
  const topLineRef = useRef<HTMLSpanElement>(null);

  const { transitionController } = useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        lineRef,
        topLineRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  const {
    onFilterClick,
    activeFilter,
    activeCountryFilter,
    newsItems,
    totalPages,
    pageIndex,
    onPaginationClick,
    showNoResultMessage,
    resetFilters,
  } = useNewsFiltering(elementRef, gridRef, blok);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash
        .replace(/[-_+#]/g, ' ')
        .trim()
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (blok.category_filter.includes(hash)) {
        onFilterClick(hash, blok.news_category_all);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blok.news_category_all]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.newsOverview, className)}
      {...sbEditable(blok)}
    >
      <div className={classNames(styles.filters)}>
        {((blok.category_filter && blok.category_filter.length > 0 && blok.news_category_all) ||
          (blok.country_filter && blok.country_filter.length > 0 && blok.news_country_all)) && (
          <>
            <span ref={topLineRef} className={classNames(styles.line, styles.topLine)} />
            <span ref={lineRef} className={classNames(styles.line)} />
          </>
        )}
        <div className={classNames(styles.filterContainer, 'grid-gutter', 'max-content-width')}>
          {blok.category_filter && blok.category_filter.length > 0 && blok.news_category_all && (
            <PrimaryDropdown
              className={classNames(styles.filter)}
              filters={[blok.news_category_all, ...blok.category_filter]}
              filterLabels={categoryLabels}
              activeFilter={activeFilter}
              type={blok.news_category_all}
              size="large"
              onFilterClick={onFilterClick}
            />
          )}
          {blok.country_filter && blok.country_filter.length > 0 && blok.news_country_all && (
            <PrimaryDropdown
              className={classNames(styles.filter)}
              filters={[blok.news_country_all, ...blok.country_filter]}
              activeFilter={activeCountryFilter}
              type={blok.news_country_all}
              size="large"
              onFilterClick={onFilterClick}
            />
          )}
        </div>
      </div>

      {showNoResultMessage &&
        ((blok.category_filter && blok.category_filter.length > 0 && blok.news_category_all) ||
          (blok.country_filter && blok.country_filter.length > 0 && blok.news_country_all)) && (
          <NoFilterResultMessage
            message={blok.no_result_message}
            cta={blok.no_result_cta}
            onResetClick={resetFilters}
          />
        )}

      <div className={classNames(styles.contentFrame, 'grid-gutter', 'max-content-width')}>
        <div ref={gridRef} className={classNames(styles.grid)}>
          {newsItems?.map((item, index) => (
            <NewsOverviewItem
              transitionInDelay={isMobileViewport ? 0 : (index % 3) * 0.15}
              className={classNames(styles.newsItem)}
              blok={{ ...item.content }}
              full_slug={item.full_slug}
              key={`${item.uuid}_${blok._uid}`}
            />
          ))}
        </div>

        <PrimaryPagination
          className={classNames(styles.pagination)}
          total={totalPages}
          activeIndex={pageIndex}
          max={5}
          onPaginationClick={onPaginationClick}
        />
      </div>
    </div>
  );
}
